import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Background, OrderPackage, Student, StudentImage, Option, ImageType, Package, Extra, Price, BackgroundSet, School } from '../models/_models';
import { Cart } from '../models';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { shouldReplaceCart, httpStatusFromError } from '../utils/cartSelfHeal';

export const CustomerContext = createContext({});

export function useCustomer() {
  return useContext(CustomerContext);
}

export const CustomerContextProvider = ({ children }) => {
  const [customer, changeValue] = useState(initialState());
  const [isInit, setIsInit] = useState(false);

  // Spraypaint can deserialize cart payloads with circular refs between
  // OrderPackage <-> StudentImage (StudentImageResource has_many
  // :order_packages, always_include_resource_ids). JSON.stringify will
  // throw on those. The React state is already updated by the caller, so
  // the app stays consistent; we just skip persisting that particular
  // snapshot to localStorage.
  const persistCustomer = (nextCustomer) => {
    if (typeof nextCustomer !== 'object') return;

    try {
      localStorage.setItem(
        'customer',
        JSON.stringify({
          ...nextCustomer,
          currentOrderPackage: undefined,
          apptCart: undefined,
          priorityCart: undefined,
        }),
      )
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('CustomerContext.setCustomer: skipped localStorage persist', err?.message);
    }
  };

  const setCustomer = changeValue;

  // Persist on COMMIT, not at dispatch time: every committed customer state —
  // object-form or functional — lands in localStorage exactly once. The old
  // dispatch-time write skipped functional updates entirely (so cart heals
  // evaporated on reload), and persisting inside the updater instead would
  // run the write during React's render-phase updater replays.
  useEffect(() => {
    if (!customer) return;
    persistCustomer(customer);
  }, [customer]);

  // Replace a dead/purchased persisted cart by SWAPPING in a saved fresh one —
  // never by nulling first. Nulling mid-session crashed pages that render
  // customer.cart.id unguarded (seniors Builder / MultiPoseEdit — the white-
  // screen class), and the null window let the create-cart effect double-mint
  // when an unrelated state change (School.find resolving) re-ran it mid-save.
  // Discards must be LOUD: if a backend regression ever starts failing the
  // cart lookup wholesale, silent discards would wipe every visitor's cart
  // with no trail to triage.
  // Live view of the latest committed customer for async callbacks: heals
  // resolve long after the closure that started them, and reading email off a
  // stale closure minted email-less replacement carts (third review round).
  const customerRef = useRef(customer);
  customerRef.current = customer;

  const replacingCartRef = useRef(false);
  const cartValidatedRef = useRef(false);
  const replaceDeadCart = (deadCartId, reason) => {
    if (replacingCartRef.current) return;
    replacingCartRef.current = true;

    // eslint-disable-next-line no-console
    console.warn(`[CartSelfHeal] replacing persisted cart ${deadCartId} (${reason})`);

    // Carry the known email onto the replacement (checkout and CustomerCart
    // load the cart via the for_email filter, so an email-less cart would be
    // invisible there) — but only when one is actually set: initialState()
    // defaults email to '', and passing that would mint carts with email = ''
    // instead of NULL, corrupting every NULL-vs-blank check downstream (e.g.
    // the class-pictures contact export's where.not(email: nil) fallback).
    // Read via the ref, not the closure: the email may have been entered
    // after the effect that started this heal captured its scope.
    const knownEmail = customerRef.current?.email;
    const newCart = new Cart(knownEmail ? { email: knownEmail } : {});
    newCart.save()
      .then(() => {
        // Install ONLY if the cart we validated is still the current one — if
        // another path already swapped it (clearCart, a competing heal), do
        // not stomp their cart with ours; orphaning our unused mint is the
        // cheaper mistake, and we log it.
        setCustomer((prev) => {
          if (prev?.cart && prev.cart.id !== deadCartId) {
            // eslint-disable-next-line no-console
            console.warn(`[CartSelfHeal] cart changed during heal (now ${prev.cart.id}); leaving it, orphaning ${newCart.id}`);
            return prev;
          }
          return { ...prev, cart: newCart };
        });

        // If the email arrived while this heal was in flight (its own heal
        // attempt was dropped by the single-flight guard above), attach it so
        // the cart is visible to checkout's for_email lookup — the filter
        // requires BOTH the id and the email to match (cart_resource.rb pins
        // `.where(id:)`), so an email-less live cart is invisible there. That
        // same id-pinning means a stray emailed orphan can never hijack
        // checkout; the rules below are about correctness and data hygiene,
        // not orphan-hijack. (a) attach only if OUR cart actually installed:
        // React 17 legacy mode applies promise-callback setState
        // synchronously, so customerRef already reflects the install outcome
        // here. ⚠️ React 18 createRoot auto-batching would defer that commit
        // and `installed` would read stale — revisit this check at the
        // React 18 / G4 upgrade. (b) the client claims the email only after
        // the server accepted it — an optimistic claim that failed to save
        // would leave state/localStorage asserting an email the server row
        // doesn't have.
        const emailNow = customerRef.current?.email;
        const installed = customerRef.current?.cart?.id === newCart.id;
        if (installed && emailNow && !newCart.email) {
          newCart.email = emailNow;
          newCart.save()
            .then(() => setCustomer((prev) => (
              // Object IDENTITY, not id equality: a same-id cart object
              // installed by a competing path (email-sync re-fetch with a
              // corrected email) must not be stomped by our stale mint.
              prev?.cart === newCart ? { ...prev, cart: newCart } : prev
            )))
            .catch((e) => {
              // Revert AND re-commit: the install commit's persist effect can
              // run after the optimistic mutation above, so localStorage may
              // already hold the unacked email — a fresh dispatch forces the
              // persist effect to re-serialize the reverted truth.
              newCart.email = null;
              setCustomer((prev) => (
                // Same identity rule as the success dispatch above.
                prev?.cart === newCart ? { ...prev, cart: newCart } : prev
              ));
              // eslint-disable-next-line no-console
              console.error('[CartSelfHeal] failed to attach email to replacement cart:', e);
            });
        }
      })
      .catch((e) => {
        // No retry machinery — deliberately (fourth review round: the retry
        // refs added more interleavings than they removed). A failed save is
        // loud, the session keeps the dead cart exactly as pre-heal, and the
        // dead id is still persisted — so the NEXT page load re-validates and
        // heals. A transient blip costs one visit, never the customer's
        // storage-clearing ritual.
        // eslint-disable-next-line no-console
        console.error('[CartSelfHeal] failed to save replacement cart (will heal on next page load):', e);
      })
      .finally(() => { replacingCartRef.current = false; });
  };

  useEffect(() => {
    const tempCustomer = localStorage.getItem('customer')
    if (tempCustomer && JSON.parse(tempCustomer)) {
      const school = JSON.parse(tempCustomer).currentSchool

      if (!school) return

      // Merge into prev, NOT the mount-time snapshot: initialState() already
      // loaded the snapshot, and by the time this resolves the G1 self-heal
      // may have replaced a dead cart. Rebuilding from JSON.parse(tempCustomer)
      // here re-installed the dead cart over the heal (race on which request
      // resolved first) and re-persisted it — resurrecting the #4641 wedge.
      School
        .find(school.id)
        .then((res) => setCustomer((prev) => ({ ...prev, currentSchool: res.data })))
        // Keep the persisted school on failure (state already holds it via
        // initialState), but leave a trail — a deleted school id or auth
        // regression should be visible, not silent.
        // eslint-disable-next-line no-console
        .catch((e) => console.warn('CustomerContext: school refresh failed, keeping persisted school', e?.message));
    }
  }, []);

  // G1 — validate the persisted cart before trusting it. A cart that was deleted, is no longer
  // owned by this browser session (the server identity rotated), or is already purchased must
  // NOT be reused: every request against it 404s and the page wedges until the customer clears
  // cookies (the "clear cookies fixes it" class — #4641). If it's dead/purchased, drop it so the
  // "no cart -> create one" effect below silently makes a fresh cart. A live, unpurchased cart is
  // ALWAYS kept (never lose a parent's in-progress selections); a transient lookup failure keeps
  // it too. Runs once, against the initially-restored cart id.
  useEffect(() => {
    if (cartValidatedRef.current) return;
    const cartId = customer?.cart?.id;
    if (!cartId) { cartValidatedRef.current = true; return; }
    // Avoid a double lookup: when an email is present, the email-sync effect below already
    // fetches this cart (to attach the email) and now self-heals on 404/403 (G2), so it covers
    // validation. G1 only needs to validate when no email-sync will run (no email yet).
    if (customer?.email) { cartValidatedRef.current = true; return; }
    cartValidatedRef.current = true;

    Cart.find(cartId)
      .then((res) => {
        const cart = res?.data;
        if (shouldReplaceCart({ found: !!cart, purchased: !!cart?.purchased, httpStatus: null })) {
          replaceDeadCart(cartId, 'purchased or missing');
        }
      })
      .catch((err) => {
        const httpStatus = httpStatusFromError(err);
        if (shouldReplaceCart({ found: null, httpStatus })) {
          replaceDeadCart(cartId, `HTTP ${httpStatus}`);
        } else {
          // Transient error (5xx / network): we KEEP the cart, but log it so real backend
          // problems stay visible (matches the sibling email-sync catch below).
          // eslint-disable-next-line no-console
          console.error('CustomerContext: kept cart after a transient validation error', err);
        }
      });
  }, [customer?.cart?.id]);

  // First-visit cart creation. Self-heals no longer pass through here (they
  // swap dead -> fresh atomically via replaceDeadCart), so this only fires
  // when there is genuinely no cart. The in-flight ref closes a pre-existing
  // race: this deep-compare effect re-runs on ANY customer change, and a
  // second run while the first save was still pending minted duplicate carts
  // (the customer's selections could land on the orphaned one).
  const creatingCartRef = useRef(false);
  useDeepCompareEffect(() => {
    if (customer && customer.cart) return;
    if (creatingCartRef.current) return;
    creatingCartRef.current = true;

    // Plain Cart() when no email is known — email: '' would mint a blank-string
    // email server-side (see replaceDeadCart).
    const newCart = new Cart(customer?.email ? { email: customer.email } : {});

    newCart.save()
      .then(() => setCustomer((prev) => ({ ...prev, cart: newCart })))
      .catch((e) => console.error('Failed to create cart:', e))
      .finally(() => { creatingCartRef.current = false; });
  }, [customer || {}]);

  useEffect(() => {
    if (!customer) return;
    if (!customer.email) return;

    const keys = ['priorityCart', 'apptCart', 'cart'];

    keys.forEach((async (key) => {
      if (!customer[key]) return;

      try {
        const cart = await Cart.find(customer[key].id).then((res) => res.data);

        if (!cart) return;

        // A cart that loaded fine but is already PURCHASED must also self-heal (same rule as
        // G1): a returning customer whose email is set — i.e. anyone who has bought before —
        // otherwise keeps the checked-out cart and dead-ends at the EmptyCart screen.
        // Healing is scoped to the main cart ONLY — see the catch below for why.
        if (key === 'cart' && shouldReplaceCart({ found: true, purchased: !!cart.purchased, httpStatus: null })) {
          replaceDeadCart(cart.id, 'purchased');
          return;
        }

        cart.email = customer.email;
        await cart.save();
        cart.email = customer.email;

        setCustomer((prev) => ({ ...prev, [key]: cart }));
      } catch (e) {
        // G2 — a dead / unowned MAIN cart here (404/403) must SELF-HEAL, not just log: otherwise
        // the stale id keeps 404ing on every request. Drop it (the create-cart effect makes a
        // fresh one). Transient errors are left alone so a blip never nukes a good cart.
        //
        // apptCart / priorityCart deliberately keep the old log-only behavior: nothing
        // re-creates them (the create-cart effect only mints `cart`), several call sites
        // fall back with `customer.priorityCart ? 'priorityCart' : 'cart'` (nulling would
        // silently flip checkout onto the WRONG cart), and appointment-flow components
        // dereference customer.apptCart.id unguarded (nulling mid-flow would crash them).
        const httpStatus = httpStatusFromError(e);
        if (key === 'cart' && shouldReplaceCart({ found: null, httpStatus })) {
          replaceDeadCart(customer[key]?.id, `HTTP ${httpStatus}`);
        } else {
          // eslint-disable-next-line no-console
          console.error(`Failed to update ${key} cart:`, e);
        }
      }
    }));
  }, [customer && customer.email]);

  const clearCart = () => {
    // Same email-or-nothing rule as replaceDeadCart: email '' must not reach
    // the server as a blank string (NULL-vs-blank checks depend on it).
    const newCart = new Cart(customer?.email ? { email: customer.email } : {});

    newCart.save().then(() => setCustomer((prev) => ({ ...prev, cart: newCart, apptCart: undefined })));
  }

  return (
    <CustomerContext.Provider value={{ customer, setCustomer, clearCart }}>
      {children}
    </CustomerContext.Provider>
  );
};

function initialState() {
  const storedCustomer = localStorage.getItem('customer');

  if (storedCustomer) {
    const parsed = JSON.parse(storedCustomer);

    if (Array.isArray(parsed.cart)) {
      localStorage.removeItem('customer');

      return { cart: null, email: '', currentSchool: null, currentStudent: null, currentOrderPackage: null };
    }

    return rehydrate(parsed);
  }

  const newCustomer = { cart: null, email: '', currentSchool: null, currentStudent: null, currentOrderPackage: null }

  return newCustomer;
}

function rehydrate(customer) {
  // const { currentStudent } = customer;
  // FIXME: passing cart to constructor throws error
  // TODO: this whole process kind of sucks actually. need a better way to store temporary models in the client
  // if (customer.cart) customer.cart = new Cart(customer.cart);
  // if (currentStudent) customer.currentStudent = new Student(currentStudent);

  return customer;
}
